/**
 * Chart themes page (screenshots/80): list · editor · preview. Saves to `/api/themes`, registers each theme with
 * ECharts (themeStore), and exports/imports ECharts Theme Builder JSON. Checks are the dataviz validator's
 * (paletteChecks.ts via themeChecks.ts), updated as you type.
 * Team themes and "Team default" are for owners/admins (`canTeam`); anyone can make personal themes.
 */
import { useMemo, useRef, useState } from "react";
import { navigate, plainClick } from "../../app/router";
import { Switch } from "../../components/shared/ui";
import { toastStore } from "../../stores/toastStore";
import { CIcon } from "./icons";
import { Seg } from "./settings/ui";
import { ThemeEditor } from "./ThemeEditor";
import { DeleteThemeDialog, ThemeConflictPanel } from "./ThemeConflicts";
import { ThemePreviewGrid, type SeeAs } from "./ThemePreview";
import { themeSummary } from "./themeChecks";
import { BUILTIN_THEMES, fromEChartsTheme, type ThemeMerge, slugify, themeSnippet, toEChartsTheme, type ChartTheme } from "./themes";
import { useThemes, type SaveOutcome, type ThemeStore } from "./themeStore";
import { Crumbs, sectionCrumb } from "../../app/crumbs";

type Usage = Record<string, { overview: string; charts: number; note?: string }[]>;

export function ThemeBuilder({ store, usage, onSaved, onDeleted, canTeam = false }: { store: ThemeStore; usage: Usage; onSaved?: (t: ChartTheme) => void; onDeleted?: (id: string) => void; canTeam?: boolean }) {
  const themes = useThemes(store, (s) => s.themes);
  const selectedId = useThemes(store, (s) => s.selectedId);
  const draft = useThemes(store, (s) => s.draft);
  const teamDefault = useThemes(store, (s) => s.teamDefault);
  const shown = draft ?? themes.find((t) => t.id === selectedId) ?? themes[0];
  const conflict = useThemes(store, (s) => s.conflict);
  const [deleting, setDeleting] = useState(false);
  const summary = useMemo(() => themeSummary(shown), [shown]);
  const [as, setAs] = useState<SeeAs>("normal");
  const [mtab, setMtab] = useState<"edit" | "preview">("edit");   // phones (91): Edit · Preview
  const [updateUses, setUpdateUses] = useState(true);
  const file = useRef<HTMLInputElement>(null);
  const scope = canTeam ? "team" : "user";
  const count = (id: string) => (usage[id] ?? []).reduce((a, u) => a + u.charts, 0);
  const unsaved = (id: string) => !themes.some((t) => t.id === id);

  const importJson = async (f: File) => {
    try {
      const json = JSON.parse(await f.text());
      const name = (typeof json.themeName === "string" && json.themeName) || f.name.replace(/\.json$/, "");
      const t = fromEChartsTheme(json, { id: slugify(name), name, scope });
      const id = store.getState().createFrom("civic-light", t.name, scope);
      store.getState().edit(() => ({ ...t, id, scope }));
    } catch { toastStore.getState().show({ message: "That file isn’t ECharts theme JSON." }); }
  };
  const save = async () => {
    const d = store.getState().draft;
    const r: SaveOutcome = await store.getState().save().catch(() => ({ ok: false, problems: ["Couldn’t save"] }));
    if (!r.ok) { if (!store.getState().conflict) toastStore.getState().show({ message: r.problems[0] }); return; }
    // 90: nothing overlapped, so it saved straight away; say what came from the other edit.
    if (r.merged?.length) toastStore.getState().show({ message: `Merged: ${r.merged.join(", ").toLowerCase()}` });
    if (d) onSaved?.(store.getState().draft ?? d);
  };
  const resolve = async (keep: Record<string, "mine" | "theirs">) => {
    const r = await store.getState().resolve(keep).catch(() => ({ ok: false, problems: ["Couldn’t save"] }));
    if (!r.ok) { toastStore.getState().show({ message: r.problems[0] }); return; }
    const d = store.getState().draft;
    if (d) onSaved?.(d);
  };
  const remove = async () => {
    const id = shown.id;
    setDeleting(false);
    store.getState().remove(id);
    toastStore.getState().show({ message: `Deleted “${shown.name}”` });
    onDeleted?.(id);
  };
  const fallbackName = (BUILTIN_THEMES.concat(themes).find((t) => t.id === (teamDefault === shown.id ? "civic-light" : teamDefault)) ?? BUILTIN_THEMES[0]).name;
  const list = (s: ChartTheme["scope"]) => [...themes.filter((t) => t.scope === s), ...(draft && unsaved(draft.id) && draft.scope === s ? [draft] : [])];

  return (
    <div className="theme-builder">
      <header className="theme-builder__top">
        <div><Crumbs trail={[sectionCrumb("overview"), { label: "Chart settings" }, { label: "Themes" }]} />
          <h1>Chart themes</h1></div>
        <input ref={file} type="file" accept="application/json,.json" hidden onChange={(e) => { if (e.target.files?.[0]) void importJson(e.target.files[0]); e.target.value = ""; }} />
        <button type="button" className="btn btn--secondary" onClick={() => file.current?.click()}><CIcon name="upload" />Import ECharts JSON</button>
        <button type="button" className="btn btn--primary" onClick={() => store.getState().createFrom(selectedId, "New theme", scope)}><CIcon name="plus" />New theme</button>
      </header>
      <div className="theme-mhead">
        <button type="button" className="icon-btn" aria-label="Back to the overview" onClick={() => navigate("/dashboard")}><CIcon name="chevron-left" size={18} /></button>
        <h1>{shown.name}</h1><button type="button" className="link-button" disabled={!draft} onClick={() => void save()}>Save</button>
        <Seg wide label="Theme view" value={mtab} onChange={setMtab} options={[{ id: "edit", label: "Edit" }, { id: "preview", label: "Preview" }]} />
      </div>
      <div className="theme-builder__body" data-mtab={mtab}>
        <nav className="theme-list" aria-label="Themes">
          {([["team", "Your team"], ["user", "Yours"], ["builtin", "Built in"]] as const).map(([s, label]) => {
            const items = list(s);
            if (!items.length) return null;
            return (
              <div key={s} className="theme-list__group"><h2 className="eyebrow">{label}</h2>
                {items.map((t) => (
                  <button key={t.id} type="button" className={`theme-card${t.id === shown.id ? " is-on" : ""}`} aria-current={t.id === shown.id}
                    onClick={() => store.getState().select(t.id)}>
                    <span className="theme-card__top"><strong>{t.name}</strong>
                      {t.id === teamDefault ? <span className="chip chip--neutral">Team default</span> : unsaved(t.id) || (t.scope !== "builtin" && !count(t.id)) ? <span className="chip chip--neutral">Draft</span> : null}</span>
                    <span className="theme-card__dots">{t.palette.slice(0, 6).map((c, i) => <i key={i} style={{ background: c }} />)}</span>
                    <small>{t.scope === "builtin" ? `${t.description ?? ""}${count(t.id) ? ` · used by ${count(t.id)}` : ""}`
                      : count(t.id) ? `Used by ${count(t.id)} charts on ${usage[t.id].length} overview${usage[t.id].length === 1 ? "" : "s"}` : "Draft · not used yet"}</small>
                  </button>))}
              </div>);
          })}
          <button type="button" className="theme-new" onClick={() => store.getState().createFrom(selectedId, "New theme", scope)}><CIcon name="plus" />New theme</button>
          <div className="theme-import"><button type="button" className="link-button" onClick={() => file.current?.click()}><CIcon name="upload" />Import from ECharts Theme Builder</button>
            <p>Drop an ECharts theme .json</p></div>
        </nav>

        <ThemeEditor theme={shown} editable={!!draft} isDefault={teamDefault === shown.id} canTeam={canTeam} problems={summary.problems}
          edit={(fn) => store.getState().edit(fn)} onSave={() => void save()}
          onCopy={() => store.getState().createFrom(shown.id, `${shown.name} copy`, scope)}
          onRebase={(id) => { const b = BUILTIN_THEMES.find((x) => x.id === id); if (b) store.getState().edit((x) => ({ ...b, id: x.id, name: x.name, description: x.description, scope: x.scope, basedOn: b.id })); }}
          onTeamDefault={(on) => { if (on && !unsaved(shown.id)) store.getState().setTeamDefault(shown.id); }}
          onDelete={shown.scope !== "builtin" && !unsaved(shown.id) && (shown.scope === "user" || canTeam) ? () => setDeleting(true) : undefined}
          conflict={conflict ? <ThemeConflictPanel merge={conflict.merge as ThemeMerge} by={conflict.by} theirsAt={conflict.theirs.updatedAt} onResolve={(k) => void resolve(k)} /> : undefined} />
        {deleting && <DeleteThemeDialog name={shown.name} usage={usage[shown.id] ?? []} fallback={fallbackName} onCancel={() => setDeleting(false)} onDelete={() => void remove()} />}

        <section className="theme-preview" aria-label="Preview">
          <div className="theme-preview__bar"><span className="spacer" /><span className="theme-preview__as">See it as</span>
            <Seg label="See it as" value={as} onChange={setAs}
              options={[{ id: "normal", label: "Normal" }, { id: "protan", label: "Protan" }, { id: "deutan", label: "Deutan" }, { id: "tritan", label: "Tritan" }, { id: "gray", label: "Gray" }]} /></div>
          <ThemePreviewGrid theme={shown} as={as} />
          <div className="theme-preview__cards">
            <div className="theme-checks"><div className="theme-checks__head"><strong>Palette checks</strong>{summary.problems > 0 && <span className="chip chip--negative">{summary.problems} to fix</span>}</div>
              <ul className="cs-checks">{summary.lines.map((l, i) => <li key={i} className={`is-${l.state}`}>
                <CIcon name={l.state === "pass" ? "circle-check" : l.state === "warn" ? "triangle-alert" : l.state === "info" ? "circle" : "circle-x"} size={13} />{l.text}</li>)}</ul></div>
            <div className="theme-export"><div className="theme-export__head"><strong>Use in ECharts</strong>
              <button type="button" className="btn btn--secondary btn--small" onClick={() => { void navigator.clipboard?.writeText(JSON.stringify(toEChartsTheme(shown), null, 2)); }}><CIcon name="copy" />Copy JSON</button>
              <a className="icon-btn" aria-label="Download theme JSON" download={`${shown.id}.json`}
                href={`data:application/json;charset=utf-8,${encodeURIComponent(JSON.stringify({ themeName: shown.name, theme: toEChartsTheme(shown) }, null, 2))}`}><CIcon name="download" size={16} /></a></div>
              <pre tabIndex={0} aria-label="ECharts theme code"><code>{themeSnippet(shown)}</code></pre></div>
          </div>
          {(usage[shown.id]?.length ?? 0) > 0 && (
            <div className="theme-usage"><div className="theme-usage__head"><strong>Where it’s used · {count(shown.id)} charts</strong><span className="spacer" />
              <span className="theme-usage__toggle"><Switch on={updateUses} label="Update them when I save" onChange={setUpdateUses} />Update them when I save</span></div>
              {usage[shown.id].map((u) => (
                <div key={u.overview} className="theme-usage__row"><CIcon name="layout-dashboard" size={14} /><strong>{u.overview}</strong>
                  <span>{u.charts} chart{u.charts === 1 ? "" : "s"}{u.note ? ` · ${u.note}` : ""}</span>
                  <button type="button" className="link-button" onClick={() => navigate("/dashboard")}>Preview</button></div>))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
